import React from 'react';
import { X, Calendar, User, Activity, CreditCard, Users, Package, Server, Monitor, AlertCircle, Info, XCircle } from 'lucide-react';
import { Button } from '../ui/Button';

interface LogDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  log: {
    _id: string;
    level: string;
    message: string;
    action: string;
    category: string;
    subcategory: string;
    userId: number;
    username: string;
    targetId?: number;
    targetType?: string;
    details: any;
    ipAddress?: string;
    userAgent?: string;
    timestamp: string;
  } | null;
}

const LogDetailsModal: React.FC<LogDetailsModalProps> = ({ isOpen, onClose, log }) => {
  if (!isOpen || !log) return null;

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warn':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'debug':
        return <Activity className="w-5 h-5 text-gray-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'auth':
        return <User className="w-5 h-5 text-green-500" />;
      case 'user':
        return <Users className="w-5 h-5 text-blue-500" />;
      case 'line':
        return <Monitor className="w-5 h-5 text-purple-500" />;
      case 'credit':
        return <CreditCard className="w-5 h-5 text-yellow-500" />;
      case 'system':
        return <Server className="w-5 h-5 text-gray-500" />;
      case 'cms':
        return <Activity className="w-5 h-5 text-indigo-500" />;
      case 'p2p':
        return <Package className="w-5 h-5 text-orange-500" />;
      case 'package':
        return <Package className="w-5 h-5 text-pink-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDetails = (details: any) => {
    if (!details) return 'Nenhum detalhe disponível';
    
    if (typeof details === 'string') {
      return details;
    }
    
    if (typeof details === 'object') {
      // Função para converter timestamps Unix para formato legível
      const convertTimestamps = (obj: any): any => {
        if (Array.isArray(obj)) {
          return obj.map(convertTimestamps);
        } else if (obj !== null && typeof obj === 'object') {
          const converted: any = {};
          for (const [key, value] of Object.entries(obj)) {
            // Verifica se é um timestamp Unix (número de 10 dígitos)
            if (typeof value === 'number' && value > 1000000000 && value < 2000000000) {
              const date = new Date(value * 1000);
              converted[key] = `${value} (${date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })})`;
            } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
              // Converte strings ISO para formato brasileiro
              const date = new Date(value);
              converted[key] = `${value} (${date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })})`;
            } else {
              converted[key] = convertTimestamps(value);
            }
          }
          return converted;
        }
        return obj;
      };
      
      const convertedDetails = convertTimestamps(details);
      return JSON.stringify(convertedDetails, null, 2);
    }
    
    return String(details);
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'warn':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'debug':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'auth':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'user':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'line':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'credit':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'system':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'cms':
        return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'p2p':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'package':
        return 'text-pink-600 bg-pink-50 border-pink-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getLevelIcon(log.level)}
            {getCategoryIcon(log.category)}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Detalhes do Log</h2>
              <p className="text-lg text-gray-500">ID: {log._id}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="lg"
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Fechar
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="space-y-6">
            {/* Informações Básicas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Nível
                  </label>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-lg font-medium border ${getLevelColor(log.level)}`}>
                    {getLevelIcon(log.level)}
                    {log.level.toUpperCase()}
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-lg font-medium border ${getCategoryColor(log.category)}`}>
                    {getCategoryIcon(log.category)}
                    {log.category.charAt(0).toUpperCase() + log.category.slice(1)}
                    {log.subcategory && (
                      <span className="text-xs opacity-75">
                        ({log.subcategory})
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Ação
                  </label>
                  <p className="text-lg text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                    {log.action}
                  </p>
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Data/Hora
                  </label>
                  <p className="text-lg text-gray-900 bg-gray-50 px-3 py-2 rounded border flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {formatTimestamp(log.timestamp)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-1">
                    Usuário
                  </label>
                  <p className="text-lg text-gray-900 bg-gray-50 px-3 py-2 rounded border flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {log.username || 'Sistema'}
                    {log.userId && (
                      <span className="text-xs text-gray-500">(ID: {log.userId})</span>
                    )}
                  </p>
                </div>

                {log.targetId && (
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-1">
                      Alvo da Ação
                    </label>
                    <p className="text-lg text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                      {log.targetType ? log.targetType.charAt(0).toUpperCase() + log.targetType.slice(1) : 'Desconhecido'} ID: {log.targetId}
                    </p>
                  </div>
                )}

                {log.ipAddress && (
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-1">
                      Endereço IP
                    </label>
                    <p className="text-lg text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                      {log.ipAddress}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Mensagem */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-2">
                Mensagem
              </label>
              <p className="text-lg text-gray-900 bg-gray-50 px-4 py-3 rounded border">
                {log.message}
              </p>
            </div>

            {/* Detalhes */}
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-2">
                Detalhes Técnicos
              </label>
              <div className="bg-gray-900 text-green-400 p-4 rounded border font-mono text-lg overflow-x-auto">
                <pre>{formatDetails(log.details)}</pre>
              </div>
            </div>

            {/* User Agent */}
            {log.userAgent && (
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  User Agent
                </label>
                <p className="text-lg text-gray-600 bg-gray-50 px-3 py-2 rounded border break-all">
                  {log.userAgent}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Fechar
          </Button>
          <Button
            onClick={() => {
              // Copiar detalhes para clipboard
              const logText = `Log ID: ${log._id}
Nível: ${log.level}
Categoria: ${log.category}
Ação: ${log.action}
Mensagem: ${log.message}
Usuário: ${log.username}
Data: ${formatTimestamp(log.timestamp)}
Detalhes: ${formatDetails(log.details)}`;
              
              navigator.clipboard.writeText(logText).then(() => {
                alert('Detalhes copiados para a área de transferência!');
              });
            }}
          >
            Copiar Detalhes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogDetailsModal;
